'use client'
import { useEffect, useMemo } from 'react'

import { Editor, useEditor } from '@tiptap/react'

import ExtensionKit from '../extensions/extension-kit.js'
import { EditorUser } from '../features/BlockEditor/types.js'
import { useSidebar } from './useSidebar.js'

import TurndownService from 'turndown'
import { gfm } from 'turndown-plugin-gfm'
import { AssetReturnType, openAssetHQType } from '../types.js'
import i18next from 'i18next'

declare global {
  interface Window {
    editor: Editor | null
  }
}

const dummyAssetHqHandler = (listener: (asset: AssetReturnType) => void, file?: File) => {}

export const useBlockEditor = ({
  content,
  handleChange,
  openAssetHQHandler,
  additionalContext,
  autoFocus,
  isEditable,
  dir,
}: {
  content: { html: string; json: any }
  handleChange?: (data: any) => void
  openAssetHQHandler?: openAssetHQType
  additionalContext?: Record<string, any>
  autoFocus: boolean
  isEditable?: boolean
  dir?: 'ltr' | 'rtl'
}) => {
  const leftSidebar = useSidebar()

  const turndownService = new TurndownService({})
  // Use the gfm plugin
  turndownService.use(gfm)
  turndownService.addRule('twitter', {
    filter: 'div',
    replacement: function (content) {
      return '\n\n' + content + '\n\n'
    },
  })

  const editor = useEditor(
    {
      content: content?.json ?? content ?? {},
      immediatelyRender: false,
      autofocus: autoFocus ?? true,
      editable: isEditable ?? true,
      onCreate: ({ editor }) => ({
        // provider?.on('synced', () => {
        //   if (editor.isEmpty) {
        //     editor.commands.setContent(initialContent)
        //   }
        // })
      }),
      extensions: [
        ...ExtensionKit({
          dir: dir || i18next.dir(),
          openAssetHQHandler: openAssetHQHandler ?? dummyAssetHqHandler,
          // provider,
        }),
        // Collaboration.configure({
        //   document: ydoc,
        // }),
        // CollaborationCursor.configure({
        //   provider,
        //   user: {
        //     name: randomElement(userNames),
        //     color: randomElement(userColors),
        //   },
        // }),
      ],
      editorProps: {
        attributes: {
          languages: i18next.language,
          autocomplete: 'off',
          autocorrect: 'off',
          autocapitalize: 'off',
          class: 'min-h-full',
        },
      },
      onUpdate: ({ editor }) => {
        handleChange(editor?.getJSON() ?? {})
      },
    },
    [],
  )

  useEffect(() => {
    if (editor?.storage?.aiCommand) {
      editor.storage.aiCommand.title = additionalContext?.title ?? ''
      editor.storage.aiCommand.language = additionalContext?.language ?? ''
    }
  }, [editor, additionalContext])

  const users = useMemo(() => {
    if (!editor?.storage?.collaborationCursor?.users) {
      return []
    }

    return editor?.storage?.collaborationCursor?.users.map((user: EditorUser) => {
      const names = user.name?.split(' ')
      const firstName = names?.[0]
      const lastName = names?.[names.length - 1]
      const initials = `${firstName?.[0] || '?'}${lastName?.[0] || '?'}`

      return { ...user, initials: initials.length ? initials : '?' }
    })
  }, [editor?.storage.collaborationCursor?.users])

  const characterCount = editor?.storage.characterCount || {
    characters: () => 0,
    words: () => 0,
  }

  // useEffect(() => {
  //   provider?.on('status', (event: { status: WebSocketStatus }) => {
  //     setCollabState(event.status)
  //   })
  // }, [provider])

  if (typeof window != 'undefined') window.editor = editor

  return { editor, users, characterCount, leftSidebar }
}
